/**
 * Recentes e contador de uso do Localizador, por usuário, em `localStorage`.
 *
 * Mesmo padrão de persistência local da Sidebar (`lume:sidebar:expanded`).
 *
 * `nowMs` é injetado em `pushRecent` de propósito: teste que depende do relógio
 * é teste que falha em CI por motivo errado.
 *
 * Toda leitura degrada para vazio em conteúdo corrompido. A paleta é a saída de
 * emergência do operador — ela abre em qualquer circunstância, mesmo que o
 * histórico esteja perdido.
 */
import type { SpotlightItem, SpotlightSectionId } from './types';

const RECENTS_KEY = 'lume:spotlight:recents';
const USAGE_KEY = 'lume:spotlight:usage';

export const RECENTS_CAP = 8;

export interface RecentEntry {
  key: string;
  section: SpotlightSectionId;
  label: string;
  /** Guardado junto para a linha de recente ser clicável sem novo fetch. */
  to: string;
  at: number;
}

function readJson<T>(key: string, fallback: T): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw !== null ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Cota cheia ou modo privado: perder o histórico é aceitável; quebrar não.
  }
}

export function readRecents(): RecentEntry[] {
  const rows = readJson<RecentEntry[]>(RECENTS_KEY, []);
  return Array.isArray(rows) ? (rows as RecentEntry[]) : [];
}

export function pushRecent(item: SpotlightItem, nowMs: number): void {
  const entry: RecentEntry = {
    key: item.key,
    section: item.section,
    label: item.label,
    to: item.to,
    at: nowMs,
  };
  const next = [entry, ...readRecents().filter((r) => r.key !== item.key)].slice(0, RECENTS_CAP);
  writeJson(RECENTS_KEY, next);
}

export function readUsage(): Record<string, number> {
  const raw = readJson<Record<string, number>>(USAGE_KEY, {});
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return {};
  return raw as Record<string, number>;
}

export function bumpUsage(section: SpotlightSectionId): void {
  const usage = readUsage();
  writeJson(USAGE_KEY, { ...usage, [section]: (usage[section] ?? 0) + 1 });
}
